import { useState } from 'react';
import type { ReactNode } from 'react';
import { cx } from '../cx';
import { devWarn } from '../dev';
import { Button } from '../Button/Button';
import { ListGroup, ListRow } from '../ListRow/ListRow';

export type ConsentCategory = {
  id: string;
  label: ReactNode;
  description?: ReactNode;
  /** Always on (strictly necessary). */ required?: boolean;
};

/** The adjust view's starting choice: required categories on, the rest off unless chosen. Pure, exported for tests. */
export function initialConsent(
  categories: ConsentCategory[],
  value?: Record<string, boolean>,
): Record<string, boolean> {
  return Object.fromEntries(categories.map((c) => [c.id, c.required ? true : value?.[c.id] === true]));
}

export type CookieBannerProps = {
  title: ReactNode;
  /** What is used and why, with a link to the cookie policy. */
  body: ReactNode;
  acceptAllLabel: ReactNode;
  necessaryOnlyLabel: ReactNode;
  settingsLabel?: ReactNode;
  onAcceptAll: () => void;
  onNecessaryOnly: () => void;
  /** Without `categories`: opens the caller's own settings. With them, the banner shows its adjust view instead. */
  onSettings?: () => void;
  /** The consent categories for the adjust view. Required ones stay on; the rest start off unless already chosen. */
  categories?: ConsentCategory[];
  /** The visitor's current choice, if any. */
  value?: Record<string, boolean>;
  /** Saves the adjusted choice. */
  onSave?: (value: Record<string, boolean>) => void;
  saveLabel?: ReactNode;
  /** Pin to the bottom of the viewport above everything (the real banner); off in the showcase. */
  fixed?: boolean;
  /** Accessible name of the region. */
  label: string;
  className?: string;
};

/**
 * The consent banner. "Only necessary" and "Accept all" carry equal visual weight (both the same
 * button style), refusing is never hidden, and nothing is pre-ticked.
 */
export function CookieBanner({
  title,
  body,
  acceptAllLabel,
  necessaryOnlyLabel,
  settingsLabel,
  onAcceptAll,
  onNecessaryOnly,
  onSettings,
  categories,
  value,
  onSave,
  saveLabel,
  fixed = false,
  label,
  className,
}: CookieBannerProps) {
  const [adjusting, setAdjusting] = useState(false);
  const [choice, setChoice] = useState<Record<string, boolean>>(() => initialConsent(categories ?? [], value));
  const canAdjust = Boolean(categories?.length && onSave);
  devWarn(
    canAdjust && !settingsLabel,
    'CookieBanner: categories and onSave need a settingsLabel, or nobody can open the adjust view.',
  );
  return (
    <section
      role="region"
      aria-label={label}
      className={cx(fixed && 'fixed inset-x-0 bottom-0 z-(--jc-z-consent) p-3 md:p-4', className)}
    >
      <div className="@container mx-auto max-w-4xl rounded-jc-container bg-jc-surface text-jc-ink shadow-jc-lg ring-1 ring-jc-card-border">
        <div className="flex flex-col gap-4 p-5 @2xl:flex-row @2xl:items-end @2xl:p-6">
          <div className="flex flex-1 flex-col gap-1">
            <h2 className="type-subheading">{title}</h2>
            <div className="text-sm text-jc-ink-secondary">{body}</div>
            {adjusting && categories ? (
              <ListGroup className="mt-3">
                {categories.map((c) => (
                  <ListRow
                    key={c.id}
                    title={c.label}
                    sub={c.description}
                    toggle={{
                      checked: choice[c.id] ?? false,
                      disabled: c.required,
                      onCheckedChange: (on) => setChoice((prev) => ({ ...prev, [c.id]: on })),
                    }}
                  />
                ))}
              </ListGroup>
            ) : null}
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {adjusting && onSave ? (
              <Button variant="tertiary" size="sm" onClick={() => onSave(choice)}>
                {saveLabel}
              </Button>
            ) : settingsLabel && (canAdjust || onSettings) ? (
              <Button
                variant="tertiary"
                size="sm"
                aria-expanded={canAdjust ? adjusting : undefined}
                onClick={() => (canAdjust ? setAdjusting(true) : onSettings?.())}
              >
                {settingsLabel}
              </Button>
            ) : null}
            <Button variant="action" size="sm" onClick={onNecessaryOnly}>
              {necessaryOnlyLabel}
            </Button>
            <Button variant="action" size="sm" onClick={onAcceptAll}>
              {acceptAllLabel}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
